/**
 * ImportSelectionBar — the compact "current import" control both AI Review and
 * Backchannel Tree render under their `PageHeading`.
 *
 * Three states:
 *
 *   nothing selected      the finder (`ImportPicker`), expanded, no Cancel
 *   selected + usable     one collapsed row: filename, tags, the linked SEARCH
 *                         (id → Thrive, name → Search Detail), the contact
 *                         count, owner, date — plus Change
 *   selected + not usable the collapsed row PLUS a warning naming the reason, and
 *                         the finder expanded with unusable rows disabled in place
 *
 * "Not usable" is per page, not global (`requirements.ts`). The shared selection
 * is NOT cleared when it is unusable — the recruiter goes back to AI Review and it
 * is still there. That is the point of the shared value.
 *
 * ONE predicate, two renderings: `unusable` produces both the notice above the
 * finder (long form) and each disabled row's label (short form), so a row and the
 * notice can never disagree about the same import.
 *
 * THIS COMPONENT OWNS THE SCREEN'S QUERY STRING — the deep-link params
 * (`?import=` / `?importId=`, see `reconcileUrl`) and the search narrowing
 * (`SEARCH_FILTER_PARAM`). The picker is a controlled consumer. Keep it that way.
 *
 * The narrowing is opt-in only and never seeded from the current selection, and it
 * does NOT itself hold the finder open: `changing` alone drives expansion, seeded
 * from the URL so a pasted link still opens onto the narrowed list.
 *
 * A narrowing CANNOT outlive a collapse. An earlier version of this docblock argued
 * that one could, and called it "dormant rather than hidden" — that was the defect,
 * not the design: the only collapses it survived were the two bypass paths, which
 * are exactly where the reported bug lived. Every collapse now goes through
 * `collapse()`, which drops it. Reasoning: Decision 2 of
 * `.agent/Tasks/2026-08-11-import-picker-search-context/README.md`.
 */
import type { ImportSummary, ImportTag } from "@nu-assist/contracts";
import { Button } from "@nu-assist/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { ImportsApiError, type ImportsClient } from "../../../clients/imports.client";
import { displayTitle } from "../../../routes/research/import-detail/importTitle";
import { TagPills } from "../../../routes/research/imports/TagPills";
import { ImportPicker } from "./ImportPicker";
import { ImportSearchLinks } from "./ImportSearchLinks";
import { useSelectedImport } from "./SelectedImportContext";
import { formatContactCount, formatImportDate } from "./importLabels";
import type { ImportUsability, UnusableImport } from "./requirements";
import { sameSelectedImport, toSelectedImport } from "./selectedImport";

/** Both historical param names; first match wins.
 *
 *  EXPORTED so the AI Review page's `openFinderOnMount` gate can ask the same
 *  question this bar answers. It gates on "does the URL already name a list?",
 *  and reading only `import` there would open a picker on an `?importId=`
 *  arrival the bar had already resolved — the two drifting apart is precisely
 *  what a shared constant prevents. */
export const DEFAULT_DEEP_LINK_PARAMS = ["import", "importId"] as const;

/**
 * The finder's search narrowing, in the URL under the SAME param names the
 * Imports listing uses (`ImportsPage`). Deliberately identical spelling: "filter
 * by search" is one concept, and a recruiter who copies a link between the two
 * screens should get the same view on both.
 *
 * The narrowing is NEVER seeded from the current selection's search. Doing so
 * would silently shrink the finder the moment a recruiter opens BCT — someone
 * arriving to switch to a DIFFERENT search's import would find it missing with
 * nothing on screen explaining why, which is precisely the vanishing-rows
 * failure this component's history is a fight against. It is opt-in only and it
 * announces itself with a chip.
 *
 * The "Only this search" button that used to apply it from the collapsed row is
 * GONE (2026-08-27, Trello #729 cl.8 — Jose: "it's confusing for a user"). Two
 * apply paths remain and both are covered: the finder's own search typeahead
 * (`PickerSearchFilterInput`) and a pasted/shared link. `applySearchFilter` is
 * therefore still reached, just from inside the finder rather than above it.
 */
const SEARCH_FILTER_PARAM = "search_id_thrive";
const SEARCH_FILTER_NAME_PARAM = "search_name";

/**
 * A blank param value is "no filter", never "the search whose id is the empty
 * string" — the same normalization `useImportsList` applies on the wire. Shared
 * by the render path and the `changing` initializer so the two cannot disagree.
 */
function readSearchFilterId(params: URLSearchParams): string | null {
  return params.get(SEARCH_FILTER_PARAM)?.trim() || null;
}

export interface ImportSelectionBarProps {
  client: ImportsClient;
  meEmail: string;
  /** Query params to read a deep-linked import id from. */
  deepLinkParams?: readonly string[];
  /**
   * Why an import can't be used here (`null` when it can). ONE predicate for two
   * renderings: the notice above the finder (long form, about the current
   * selection) and each unusable finder row (short form, disabled in place).
   * Replaced a `filter` + `requirement` pair that had to be kept in lockstep.
   */
  unusable?: (item: ImportUsability) => UnusableImport | null;
  /** Initial owner scope for the finder (`you` | `all` | `<email>`). */
  defaultOwner?: string;
  /**
   * Veto hook. Return `false` to intercept the change (AI Review's
   * "discard unapplied review?" dialog).
   *
   * The page MUST commit through the `commit` callback handed to it, not by
   * calling the context's `select()` directly — `commit` re-enters this
   * component's single commit path and therefore keeps the URL invariant.
   * Committing raw was how the confirm branch ended up leaving `?import=` on
   * the superseded id, so a reload undid a switch the recruiter had just
   * destroyed a review to make.
   */
  onBeforeSelect?: (item: ImportSummary, commit: () => void) => boolean;
  /**
   * One lifecycle tag the finder drops server-side (see `ImportPicker`'s prop
   * of the same name). BCT passes `"bct"`; AI Review omits it. Pass it HERE,
   * not by forking the picker: the bar owns the picker instance.
   */
  excludeTag?: ImportTag | readonly ImportTag[] | null;
  /**
   * Start with the finder OPEN. Read ONCE, on mount — this is a seed, not a
   * live toggle, and nothing here can close the finder again.
   *
   * That direction is the whole safety argument. Collapse happens only from
   * explicit intent (`commitSelection`, `Cancel`, the re-pick short-circuit);
   * effects observe rather than decide the finder is done — see the note
   * further down. A prop that can only ever OPEN cannot violate that, and
   * opening IS explicit intent: it is what `Change` does.
   *
   * Used by AI Review when it arrives on `?criteriaFrom=`, where the criteria
   * are already in hand and picking the list is the only act left.
   */
  openFinderOnMount?: boolean;
}

/**
 * An advisory live region: why the shared selection can't be used here, or why
 * a deep link could not be honored.
 *
 * `role="status"` already implies `aria-live="polite"`, so the attribute is not
 * repeated. The suppression is unavoidable: biome's `useSemanticElements` wants
 * `<output>` for this role on ANY element (a `<p role="status">` is rejected
 * just the same as a div — measured, not assumed), and `<output>` is
 * form-associated while this is a standalone advisory region. Same call
 * BctPage's removed truncation notice made.
 */
function SelectionNotice({ testId, children }: { testId: string; children: string }) {
  return (
    // biome-ignore lint/a11y/useSemanticElements: see the docblock above — <output> is form-associated; this is a standalone advisory region.
    <div
      role="status"
      className="rounded-md border border-warning-strong bg-warning-muted px-4 py-3 text-body text-warning-ink"
      data-testid={testId}
    >
      {children}
    </div>
  );
}

export function ImportSelectionBar({
  client,
  meEmail,
  deepLinkParams = DEFAULT_DEEP_LINK_PARAMS,
  unusable,
  defaultOwner,
  onBeforeSelect,
  excludeTag = null,
  openFinderOnMount = false,
}: ImportSelectionBarProps) {
  const { selected, select, setSelected, clear } = useSelectedImport();
  const [searchParams, setSearchParams] = useSearchParams();
  // Seeded from the URL so a pasted link that names a narrowing opens the finder
  // showing it. ONE piece of state drives expansion — an earlier revision also
  // OR'd `searchFilterId` into `expanded` and compensated with a `setChanging`
  // in BOTH filter setters, which made `changing` mean two things at once and
  // left a commit-while-narrowed unable to collapse. Reviewer-proposed shape.
  // The INITIALIZER, not an effect: a page that mounts already asking for the
  // finder (AI Review arriving on `?criteriaFrom=`) would otherwise paint one
  // frame of the collapsed summary first — a flash of exactly the state the
  // recruiter is being asked to change.
  const [changing, setChanging] = useState(
    () => openFinderOnMount || readSearchFilterId(searchParams) !== null
  );

  // Only the ID is needed at render time (adoption, the failure notice, the
  // already-adopted seed). The PARAM NAME is deliberately not hoisted: the collapse
  // path re-derives it from the live params inside `writeCollapsedUrl`, because a name
  // captured here would be as stale as the snapshot it came from.
  let deepLinkId: string | null = null;
  for (const param of deepLinkParams) {
    const value = searchParams.get(param);
    if (value) {
      deepLinkId = value;
      break;
    }
  }

  const selectedId = selected?.importId ?? null;

  const searchFilterId = readSearchFilterId(searchParams);
  /**
   * Live mirror of the query string.
   *
   * `collapse()` can be invoked from a closure OLDER than the current render, and
   * that is part of this component's public contract, not a hypothetical: the
   * `commit` handed to `onBeforeSelect` is captured by the page and fired later
   * (`AiReviewPage` stores it in state behind its discard dialog). Anything the
   * collapse path reads must therefore come from a ref, never from the captured
   * `searchParams` — a ref reads the latest value even through a stale closure.
   *
   * The two-writes fix taught this lesson for the WRITE and it was not applied to the
   * READ. That asymmetry was a silent fourth collapse route: a stale `commit` set
   * `changing = false` while the guard, reading a snapshot from before the narrowing
   * existed, returned early and skipped the drop. Finder gone, chip gone, narrowing
   * live in the URL.
   */
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;
  const searchFilterName = searchParams.get(SEARCH_FILTER_NAME_PARAM)?.trim() || null;

  // Adopt a resolved deep link exactly once per id, so a later manual change is
  // never undone by this effect re-running.
  //
  // The initializer is load-bearing, not a micro-optimization: when the deep
  // link ALREADY matches the stored selection, `needsResolve` is false, the
  // query never runs, and a null ref would leave the id un-adopted. The first
  // manual change then flips `needsResolve` true, `getImport` resolves the OLD
  // id, and the effect snaps the selection back — on the primary entry path
  // (`ImportsTable`'s Feed-into-AI-Review / Feed-into-BCT row actions, which
  // land on `?import=`/`?importId=` with that import already selected).
  // Seeding the ref marks "already honored" up front. `useRef` runs its
  // initializer once, on mount, which is exactly the scope we want.
  const adoptedRef = useRef<string | null>(
    deepLinkId !== null && deepLinkId === selectedId ? deepLinkId : null
  );

  // Only fetch when the deep link points somewhere other than the current
  // selection AND has not been honored yet. The `adoptedRef` clause stops the
  // wasted refetch against a now-stale URL after a manual change.
  const needsResolve =
    deepLinkId !== null && deepLinkId !== selectedId && adoptedRef.current !== deepLinkId;
  const deepLinkQuery = useQuery({
    // Same key ImportDetailPage uses, so the two share one cache entry.
    queryKey: ["import-detail", deepLinkId],
    queryFn: () => client.getImport(deepLinkId ?? ""),
    enabled: needsResolve,
    retry: false,
  });

  const resolved = deepLinkQuery.data;
  useEffect(() => {
    if (!deepLinkId || adoptedRef.current === deepLinkId) return;
    if (!resolved || resolved.import_id !== deepLinkId) return;
    adoptedRef.current = deepLinkId;
    setSelected(toSelectedImport(resolved));
    // PARAMETER PRECEDENCE: with both `?import=X` and `?search_id_thrive=Y`, the
    // NARROWING WINS. X is adopted as the selection, and `changing` is left alone —
    // so the finder stays open on the filtered list, chip visible, X highlighted.
    //
    // Deliberately NOT a collapse. Three reasons:
    //
    //  1. One rule, no exceptions: "if the URL names a narrowing, the finder is open
    //     and the chip is visible." `?search_id_thrive=Y` alone already opens it via
    //     the `changing` initializer; collapsing when a second param happens to sit
    //     beside it would make one parameter's behaviour depend on another's
    //     presence, which is the inconsistency this whole change exists to remove.
    //  2. That two-param URL is the combination THE PRODUCT ITSELF GENERATES —
    //     Feed-into-BCT lands on `?importId=`, then the finder's search
    //     typeahead adds the filter. If `?import=` won, that control would emit
    //     a URL whose second parameter does nothing.
    //  3. It does not reintroduce invisible state: the invariant is "the finder never
    //     COLLAPSES with a narrowing live in the URL", and here it does not collapse.
    //
    // Discarded alternative: let `?import=` win and drop the narrowing. Rejected on
    // (1) and (2) — it reads as "a settled selection should not open a finder", which
    // is reasonable in isolation but makes the product emit a dead parameter.
    //
    // The failure path is unaffected: when resolution fails this effect returns early
    // above, so `changing` keeps its initializer value and the finder is already open
    // with the chip — "that link failed, here is your previous selection, and here is
    // the search you were working in."
  }, [deepLinkId, resolved, setSelected]);

  // A deep link that cannot be resolved must SAY so. Silently falling back to
  // the stored selection means the URL names import X, the bar shows Y, and a
  // recruiter following a shared link runs against the wrong import.
  // A deep link that cannot be resolved is recorded as its own state rather
  // than derived from `needsResolve`. Deriving it was a dead end: the flag
  // stayed true forever (nothing sets `adoptedRef` on the error path), which
  // pinned `expanded` open and left `Change` disabled with no way out.
  //
  // Marking the id adopted stops the retry loop; the separate state keeps the
  // notice visible until the URL stops naming that id — which `reconcileUrl`
  // does as soon as the recruiter picks anything, including re-picking what
  // they already had.
  const [failedDeepLinkId, setFailedDeepLinkId] = useState<string | null>(null);
  useEffect(() => {
    if (!deepLinkId || !deepLinkQuery.isError) return;
    adoptedRef.current = deepLinkId;
    setFailedDeepLinkId(deepLinkId);
  }, [deepLinkId, deepLinkQuery.isError]);
  const deepLinkFailed = failedDeepLinkId !== null && failedDeepLinkId === deepLinkId;

  // Revalidate the stored projection. It is a first-paint placeholder written
  // when the import was picked — possibly long ago — and `status`,
  // `totalContacts` and `tags` all drift server-side. Without this, a
  // still-parsing selection makes `bctUnusable` say "wait until it's ready"
  // forever (nothing would ever refresh it), and a `totalContacts: 0` captured
  // mid-parse permanently disables AI Review's Preview.
  const revalidateQuery = useQuery({
    queryKey: ["import-detail", selectedId],
    queryFn: () => client.getImport(selectedId ?? ""),
    // Skipped while a deep link is resolving: both effects call `setSelected`,
    // React runs them in declaration order within one commit, and the setter is
    // not functional — so a revalidation of the OUTGOING selection landing in
    // the same batch would overwrite the adoption that just replaced it, with
    // no path back (the adopt effect has already marked the id adopted).
    enabled: selectedId !== null && !needsResolve,
    retry: false,
    // Mirrors ImportDetailPage: poll a non-terminal parse until it settles.
    refetchInterval: (query) => (query.state.data?.status === "parsing" ? 2000 : false),
  });

  const fresh = revalidateQuery.data;
  useEffect(() => {
    if (!selected || !fresh || fresh.import_id !== selected.importId) return;
    const next = toSelectedImport(fresh);
    if (sameSelectedImport(selected, next)) return;
    setSelected(next);
  }, [selected, fresh, setSelected]);

  // The import went away server-side (deleted, or access lost). Keeping a
  // phantom selection means every Preview / Generate 404s against a bar that
  // looks perfectly valid, so drop it and fall back to the finder.
  //
  // Dropping it SILENTLY is the part that needed fixing: a page keyed on the
  // selection (BCT) reacts by clearing its companies and its preview, so a
  // recruiter mid-task watches typed work vanish with nothing on screen saying
  // why. Neither existing notice covers this — the deep-link one needs a deep
  // link, and `blockedReason` needs a selection to still exist. Hence a third
  // case, and it must outlive the `clear()` that causes it.
  const revalidateError = revalidateQuery.error;
  const [droppedFilename, setDroppedFilename] = useState<string | null>(null);
  useEffect(() => {
    if (revalidateError instanceof ImportsApiError && revalidateError.status === 404) {
      setDroppedFilename(selected?.filename ?? null);
      clear();
    }
  }, [revalidateError, clear, selected]);

  // Retire the notice as soon as anything is selected again — including
  // re-picking the same row, which is also the only way out of the cached-404
  // state (`retry: false` on the same `["import-detail", id]` key means a
  // re-pick clears again immediately, which would otherwise read as "clicking
  // that row does nothing").
  useEffect(() => {
    if (selectedId) setDroppedFilename(null);
  }, [selectedId]);

  // NOTE: this effect no longer collapses anything, and must not start again.
  //
  // It used to call `setChanging(false)` on any change of committed selection, which
  // was redundant — `commitSelection` already covers the only pick this bar makes —
  // and it could not be made compatible with the parameter-precedence rule above.
  // Collapsing unconditionally would collapse deep-link adoption, breaking that rule.
  // Collapsing only when no narrowing is live would break commit-while-narrowed in a
  // subtle way: `setSearchParams` is ASYNCHRONOUS, so this effect runs in the same
  // batch as `commitSelection` and still reads the OLD snapshot, where the narrowing
  // is alive — it would skip the collapse and leave the finder open with `Change`
  // disabled. That is precisely the third state bug this feature already fixed once,
  // reintroduced by its own fix, with an identical symptom.
  //
  // Collapse only from explicit intent: `commitSelection`, `Cancel`, and the re-pick
  // short-circuit. Effects observe; they do not decide the finder is done.

  const blockedReason = selected && unusable ? (unusable(selected)?.long ?? null) : null;
  // Deliberately NOT gated on `deepLinkFailed`: when the link fails and the
  // recruiter already has a usable selection, the notice is the whole message —
  // forcing the finder open would be the dead end this replaced. With nothing
  // selected, `!selected` opens it anyway.
  //
  // `changing` is the ONLY term describing the finder. The narrowing is not one:
  // it cannot outlive a collapse, because every path that collapses also drops it
  // (see `dropSearchFilter`'s callers). So "narrowing on, finder closed" is
  // unrepresentable rather than merely unlikely, and this expression does not need
  // to mention it.
  const expanded = !selected || blockedReason !== null || changing;

  /**
   * ONE write that both collapses the URL and reconciles the deep-link param.
   *
   * Two separate writes DO NOT WORK, and the reason is worth stating precisely
   * because a previous comment here got it backwards. `setSearchParams` is
   * `navigate` underneath, NOT `setState`: the functional form resolves `prev` from
   * the live COMMITTED location, not from a pending update. So two calls in one tick
   * both read the pre-write value and the last one wins. Writing the collapse and
   * then the reconcile meant the reconcile resurrected the narrowing params the
   * collapse had just dropped — leaving
   * `?importId=<new>&search_id_thrive=…&search_name=…`, the invariant violated in
   * the exact URL the product emits. The functional updater is not what made this
   * safe and never was; a single write is.
   *
   * Both mutations therefore happen in one updater:
   *   - always drop the narrowing params (the collapse invariant);
   *   - set the deep-link param only when one is already present and stale — never
   *     add one to a clean URL.
   *
   * The early return keeps a no-op collapse from navigating at all, which is what a
   * failed deep link depends on (see the REGRESSION test in this file's suite).
   */
  function writeCollapsedUrl(importId?: string) {
    // Every input read from the LIVE params, not the render closure — including the
    // deep-link param name and id, which are themselves derived from the query string
    // and would be equally stale.
    const live = searchParamsRef.current;
    const hadNarrowing = readSearchFilterId(live) !== null;
    let liveParamName: string | null = null;
    let liveDeepLinkId: string | null = null;
    for (const param of deepLinkParams) {
      const value = live.get(param);
      if (value) {
        liveDeepLinkId = value;
        liveParamName = param;
        break;
      }
    }
    const needsReconcile =
      importId !== undefined && liveParamName !== null && liveDeepLinkId !== importId;
    if (!hadNarrowing && !needsReconcile) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(SEARCH_FILTER_PARAM);
        next.delete(SEARCH_FILTER_NAME_PARAM);
        if (liveParamName && needsReconcile && importId !== undefined) {
          next.set(liveParamName, importId);
        }
        return next;
      },
      { replace: true }
    );
  }

  /**
   * Narrow the finder to one Thrive search, or drop the narrowing.
   *
   * Both write the URL rather than local state, so the view survives a refresh
   * and a shared link — the property that made the listing's chip worth having.
   * `search_name` rides along purely so the chip can label itself before any
   * row for that search has loaded.
   */
  function applySearchFilter(searchId: string, name: string | null) {
    // Narrowing is a browsing intent, so it opens the finder. This is the only
    // place that needs to say so: `expanded` reads `changing` alone, so without
    // this line the chip would be applied to a list nobody can see.
    setChanging(true);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(SEARCH_FILTER_PARAM, searchId);
        if (name) next.set(SEARCH_FILTER_NAME_PARAM, name);
        else next.delete(SEARCH_FILTER_NAME_PARAM);
        return next;
      },
      { replace: true }
    );
  }

  /**
   * THE single exit from the expanded finder. Invariant:
   *
   *   the finder never collapses with a narrowing still live in the URL.
   *
   * Stated over the COLLAPSE rather than over any one trigger, because a
   * `commitSelection`-only fix does not hold it: collapse is reachable from three
   * places and only one of them is a commit made in this bar.
   *
   * Called from EXPLICIT INTENT ONLY — the three places the recruiter decides they
   * are done browsing:
   *
   *   1. `commitSelection`         — a pick made in this bar
   *   2. the re-pick short-circuit — re-picking the current selection
   *   3. `Cancel`                  — the finder's own dismiss
   *
   * Deliberately NOT from either effect. Deep-link adoption must not collapse at all
   * (the narrowing wins — see the precedence note there), and a selection-changed
   * effect is redundant with `commitSelection`: deleting it left the suite green, so it
   * pinned nothing while adding a second place that could get this wrong.
   *
   * Effects observe; they never decide the finder is done. Do not call
   * `setChanging(false)` outside this helper — `grep setChanging(` should show exactly
   * two writers: `setChanging(true)` where browsing starts, and this.
   *
   * Takes the committed import id so the drop and the deep-link reconcile land in ONE
   * navigation; `Cancel` passes none. Two navigations is a BUG, and this is the
   * measured version of a claim an earlier comment got wrong: splitting the write in
   * two fails the two combination tests in this file's suite. The earlier comment
   * credited the functional `(prev) => …` updater with making two writes safe — it does
   * not. `setSearchParams` is `navigate`, not `setState`, so both calls resolve `prev`
   * from the committed location and the last one wins. Reverting the updater to a
   * captured snapshot changes no test; splitting the write changes two.
   */
  function collapse(importId?: string) {
    setChanging(false);
    writeCollapsedUrl(importId);
  }

  /**
   * The CHIP's clear only. Collapsing paths do not come through here — they use
   * `collapse()`, which writes the narrowing drop and the deep-link reconcile in a
   * single navigation (see `writeCollapsedUrl` for why two writes cannot work).
   *
   * The guard keeps a clear with nothing to clear from navigating at all. Re-derived:
   * widening it to always return fails 8 tests; removing it fails 1 (the failed
   * deep-link REGRESSION case). An earlier version of this comment said "two", which
   * was true before the selection-changed effect was deleted.
   */
  function dropSearchFilter() {
    if (readSearchFilterId(searchParamsRef.current) === null) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(SEARCH_FILTER_PARAM);
        next.delete(SEARCH_FILTER_NAME_PARAM);
        return next;
      },
      { replace: true }
    );
  }

  /**
   * The commit path for a selection the recruiter makes IN THIS BAR — a click, or a
   * vetoing page's deferred confirm.
   *
   * It is NOT the only way the finder closes, and a previous version of this comment
   * claimed otherwise in a way that cost three iterations. It said deep-link adoption
   * and "the cross-tab listener" were the two commits bypassing this function. Both
   * entries were wrong: there IS no cross-tab listener (`SelectedImportContext.tsx`
   * records that it was built and deliberately removed after two blocking defects),
   * and the enumeration MISSED a live bypass — the re-pick short-circuit in
   * `handleSelect`. The one comment whose job was to list the bypasses invented one
   * and lost one, which is why `commitSelection` and `expanded` were patched three
   * times while the real paths went unaudited.
   *
   * The current, verifiable answer: `collapse()` has exactly three callers, listed on
   * that helper, and `grep collapse()` proves it. This function is one of them.
   */
  function commitSelection(item: ImportSummary) {
    select(item);
    collapse(item.import_id);
  }

  function handleSelect(item: ImportSummary) {
    // Re-picking the row that is already selected is a no-op for the SELECTION
    // — short-circuiting keeps a vetoing page from showing a "discard and
    // switch" dialog that would then discard nothing (its reset is keyed on the
    // import id, which would not change). It is NOT a no-op for the URL: this
    // is the only way to clear a deep-link param naming an unresolvable id, so
    // reconcile before returning or the notice can never be dismissed.
    if (item.import_id === selectedId) {
      collapse(item.import_id);
      return;
    }
    // Everything past here may be vetoed, so the URL is reconciled inside
    // `commitSelection` — rewriting it before the veto would leave the address
    // bar naming an import the recruiter then declined to switch to.
    if (onBeforeSelect && onBeforeSelect(item, () => commitSelection(item)) === false) return;
    commitSelection(item);
  }

  return (
    <section
      className="flex flex-col gap-2"
      aria-label="Selected people list"
      data-testid="import-selection-bar"
    >
      {selected ? (
        <div
          className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-border bg-surface px-4 py-3"
          data-testid="import-selection-summary"
        >
          {/* The title and the count are TWO elements, and that is the fix
              rather than a refactor. They used to be one `formatImportSummaryLine`
              string in one `truncate` span, so `linkedin_profiles_2026-04-07 (1)`
              at 1280px rendered "… · 98 c…" and ate the number — while every row
              in the finder BELOW this bar gave the count its own right-aligned
              column and read fine. #711 cl.4 already carried the rule ("no
              truncation ever — text always fully viewable"); this surface simply
              did not hold it.

              The title keeps `truncate` (a contact-list name is unbounded); the
              count never may. `displayTitle` strips the stored extension, the
              same rule every other surface that prints a list's name applies. */}
          <span
            className="min-w-0 flex-1 truncate text-body text-text"
            data-testid="import-selection-title"
            title={selected.filename}
          >
            {displayTitle(selected.filename)}
          </span>
          {/* `importFormat` matters here: this bar is a SECOND surface for the
              one-pill Type (Trello #711 cl.1), and a selection whose `tags[]`
              carries no type-class tag — a selection restored from
              `localStorage` before that field existed, say — would otherwise
              render the em-dash instead of the kind it plainly is.

              The `Exported` pill this surface used to show is gone with it,
              and that is the accepted trade rather than an oversight: the
              finder ABOVE this bar still offers `exported` as a chip (which
              is why `ALL_TAG_CHIPS` exists), so "already sent to Thrive" stays
              answerable where a finder actually asks it — while the summary
              line answers the one question it is for, "which list did I
              pick?". Revisit if a recruiter reports picking an
              already-exported list by mistake. */}
          <TagPills tags={selected.tags} importFormat={selected.import_format} />
          {/* The search this import belongs to, as the linked pair the imports
              listing renders: the Thrive id out to Thrive, the name in to our
              Search Detail page. Previously the bare name as dead text. */}
          <ImportSearchLinks
            searchIdThrive={selected.searchIdThrive}
            searchName={selected.searchName}
            className="text-caption"
            testIdPrefix="import-selection-search"
          />
          {/* THE COUNT, in the space the removed "Only this search" button
              freed, and in the same column position the finder's rows put it
              (`ImportPicker.tsx` — after the search links, before the owner).
              `shrink-0` is the whole point: this element is never clipped, at
              any title length or viewport width. */}
          <span
            className="shrink-0 text-right text-body text-text"
            data-testid="import-selection-count"
          >
            {formatContactCount(selected.totalContacts)}
          </span>
          <span className="text-caption text-text-secondary">{selected.ownerLabel}</span>
          <span className="text-caption text-text-secondary">
            {formatImportDate(selected.createdAt)}
          </span>
          <Button
            variant="secondary"
            type="button"
            onClick={() => setChanging(true)}
            disabled={expanded}
            data-testid="import-selection-change"
          >
            Change
          </Button>
        </div>
      ) : null}

      {deepLinkFailed ? (
        <SelectionNotice testId="import-selection-deeplink-error">
          {`The link pointed at a people list that couldn't be opened. It may have been deleted, or you may not have access. ${
            selected ? "Showing your previous selection instead." : "Pick one below."
          }`}
        </SelectionNotice>
      ) : null}

      {droppedFilename !== null ? (
        <SelectionNotice testId="import-selection-dropped">
          {`${droppedFilename} is no longer available. It may have been deleted, or you may have lost access. Pick another people list below.`}
        </SelectionNotice>
      ) : null}

      {blockedReason ? (
        <SelectionNotice testId="import-selection-warning">{blockedReason}</SelectionNotice>
      ) : null}

      {expanded ? (
        <ImportPicker
          client={client}
          meEmail={meEmail}
          selectedId={selectedId}
          onSelect={handleSelect}
          unusable={unusable}
          defaultOwner={defaultOwner}
          excludeTag={excludeTag}
          searchIdThrive={searchFilterId}
          searchName={searchFilterName}
          onClearSearchFilter={searchFilterId ? dropSearchFilter : undefined}
          onApplySearchFilter={applySearchFilter}
          // Cancel only makes sense when there is a usable selection to fall
          // back to — not while the page is blocked on picking a valid one.
          //
          // It drops the narrowing because Cancel means "I am done looking", and a
          // narrowing is part of looking. Same reason `commitSelection` drops it;
          // the two exits agree rather than contradicting each other.
          onCancel={
            selected && !blockedReason
              ? () => {
                  collapse();
                }
              : undefined
          }
        />
      ) : null}
    </section>
  );
}
